import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Alert from '@mui/material/Alert';
import { useEffect, useRef } from 'react';

import { useToastContext } from './toast-context.js';

import type { ReactNode } from 'react';

const attrsBySeverity: Record<ToastProps['severity'], { icon: ReactNode; defaultTimeoutMs: number }> = {
	success: { icon: <CheckCircleIcon />, defaultTimeoutMs: 5_000 },
	info: { icon: <InfoIcon />, defaultTimeoutMs: 15_000 },
	warning: { icon: <WarningIcon />, defaultTimeoutMs: 15_000 },
	error: { icon: <ErrorIcon />, defaultTimeoutMs: 10_000 },
};

export type ToastProps = {
	severity: 'success' | 'info' | 'warning' | 'error';
	title: string;
	/**
	 * Duration in milliseconds before the toast automatically dismisses. Default varies by `severity` (see implementation for details).
	 */
	timeoutMs?: number;
	children?: ReactNode;
};

export function Toast({ severity, title, timeoutMs: timeoutMsOverride, children }: ToastProps) {
	const { dismiss } = useToastContext();

	const { icon, defaultTimeoutMs } = attrsBySeverity[severity];
	const timeoutMs = timeoutMsOverride ?? defaultTimeoutMs;

	const timeoutRef = useRef<number | null>(null);
	const startTimeRef = useRef<number | null>(null); // UNIX timestamp
	const remainingTimeRef = useRef<number>(timeoutMs); // ms between `startTimeRef` until when the toast should dismiss
	const progressBarRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Start the initial timeout
		startTimeRef.current = Date.now();
		timeoutRef.current = window.setTimeout(() => {
			dismiss();
		}, remainingTimeRef.current);

		return () => {
			if (timeoutRef.current !== null) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, [dismiss]);

	const handlePointerEnter = () => {
		// Pause the timeout
		if (timeoutRef.current !== null && startTimeRef.current !== null) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
			remainingTimeRef.current -= Date.now() - startTimeRef.current;
			startTimeRef.current = null;
		}

		// Pause the animation
		if (progressBarRef.current) {
			progressBarRef.current.style.animationPlayState = 'paused';
		}
	};

	const handlePointerLeave = () => {
		// Resume the timeout (only if we have remaining time and no active timeout)
		if (remainingTimeRef.current > 0 && timeoutRef.current === null) {
			startTimeRef.current = Date.now();
			timeoutRef.current = window.setTimeout(() => {
				dismiss();
			}, remainingTimeRef.current);
		}

		// Resume the animation
		if (progressBarRef.current) {
			progressBarRef.current.style.animationPlayState = 'running';
		}
	};

	return (
		<Alert
			severity={severity}
			icon={icon}
			className='relative contain-[paint]'
			onClose={dismiss}
			onPointerEnter={handlePointerEnter}
			onPointerLeave={handlePointerLeave}
		>
			<div className='flex flex-col gap-1 wrap-break-word'>
				<p className='font-semibold'>{title}</p>
				{children}
			</div>

			<div className='absolute bottom-0 left-0 h-1 w-full'>
				<style>{`
					@keyframes timeout {
						0% { width: 100%; }
						100% { width: 0%; }
					}
				`}</style>

				<div
					ref={progressBarRef}
					className='h-full bg-current/40 [animation-fill-mode:forwards]'
					style={{
						animation: `timeout ${timeoutMs}ms linear`,
					}}
				/>
			</div>
		</Alert>
	);
}
